'use client';

import { GirlReview } from '@/components/girl/girl-review';
import { useAuth } from '@bells/core/auth';
import { useLanguage } from '@bells/core/i18n';
import {
  attendantNoReviews,
  attendantReviewSingle,
  reviewCreateMessage,
  reviewNoRecords,
} from '@bells/i18n';
import { baseLocale } from '@bells/i18n/runtime';
import { AttendantProfileState } from '@bells/schema/models/attendant';
import { AttendantReviewResponse } from '@bells/schema/models/review';
import { Button } from '@bells/ui/components/button';
import { Progress } from '@bells/ui/components/progress';
import { Rating } from '@bells/ui/components/rating';
import { usePwaRouter } from '@bells/ui/utils';
import { FileSearch, Loader2, Star } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { useMemo } from 'react';

export const GirlReviews = ({
  reviews,
  isLoading,
  slug,
  setOpenReviewState,
  totalRating,
  state,
  onDelete,
}: {
  reviews: AttendantReviewResponse[];
  isLoading?: boolean;
  slug?: string;
  totalRating: number;
  setOpenReviewState: (val?: AttendantReviewResponse | null) => void;
  onDelete: (val: AttendantReviewResponse) => void;
  state?: AttendantProfileState;
}) => {
  const { isAuthed } = useAuth();
  const { language } = useLanguage();
  const isPwa = usePwaRouter();

  const router = useRouter();
  const authLink = useMemo(() => {
    {
      if (!slug) {
        return undefined;
      }

      if (isPwa) {
        return `/pwa/auth/signin/customer?callbackUrl=${encodeURIComponent(`/pwa/girl/${slug}#reviews`)}`;
      } else if (language == baseLocale) {
        return `/auth/signin/customer?callbackUrl=${encodeURIComponent(`/escort-dame/${slug}#reviews`)}`;
      } else {
        return `/${language}/auth/signin/customer?callbackUrl=${encodeURIComponent(`/${language}/escort-girl/${slug}#reviews`)}`;
      }
    }
  }, [language, isPwa, slug]);

  const total5Ratings = reviews.filter((df) => df.totalRating == 5).length;
  const total4Ratings = reviews.filter((df) => df.totalRating == 4).length;
  const total3Ratings = reviews.filter((df) => df.totalRating == 3).length;
  const total2Ratings = reviews.filter((df) => df.totalRating == 2).length;
  const total1Ratings = reviews.filter((df) => df.totalRating == 1).length;

  return (
    <div className='flex flex-col gap-4'>
      <div className='flex w-full flex-col gap-4 md:gap-8 lg:flex-row lg:gap-48'>
        <div
          className='inline-flex flex-col items-center gap-2 lg:items-start'
          itemProp='aggregateRating'
          itemScope
          itemType='https://schema.org/AggregateRating'
        >
          {totalRating <= 0 ? (
            <div
              className='break-words text-base font-bold'
              itemProp='ratingValue'
            >
              {attendantNoReviews()}
            </div>
          ) : (
            <div
              className='text-3xl font-bold lg:text-6xl'
              itemProp='ratingValue'
            >
              {totalRating.toFixed(1)}
            </div>
          )}
          <Rating
            rating={totalRating}
            totalStars={5}
            variant='yellow'
            disabled
          ></Rating>
          <div className='text-xs text-paragraph'>
            <span itemProp='reviewCount' className='sr-only'>
              {reviews.length ?? 0}
            </span>
            {attendantReviewSingle({ count: reviews.length })}
          </div>
        </div>
        <ul className='flex w-full flex-1 list-none flex-col gap-2 lg:border-l lg:pl-4'>
          <li className='flex w-full items-center gap-2'>
            <span className='w-4 text-xs font-bold'>5</span>
            <div className='flex-1'>
              <Progress
                color={'success'}
                value={(total5Ratings / Math.max(reviews.length, 1)) * 100}
              ></Progress>
            </div>
          </li>
          <li className='flex w-full items-center gap-2'>
            <span className='w-4 text-xs font-bold'>4</span>
            <div className='flex-1'>
              <Progress
                color={'purple'}
                value={(total4Ratings / Math.max(reviews.length, 1)) * 100}
              ></Progress>
            </div>
          </li>
          <li className='flex w-full items-center gap-2'>
            <span className='w-4 text-xs font-bold'>3</span>
            <div className='flex-1'>
              <Progress
                color={'warning'}
                value={(total3Ratings / Math.max(reviews.length, 1)) * 100}
              ></Progress>
            </div>
          </li>
          <li className='flex w-full items-center gap-2'>
            <span className='w-4 text-xs font-bold'>2</span>
            <div className='flex-1'>
              <Progress
                color={'info'}
                value={(total2Ratings / Math.max(reviews.length, 1)) * 100}
              ></Progress>
            </div>
          </li>
          <li className='flex w-full items-center gap-2'>
            <span className='w-4 text-xs font-bold'>1</span>
            <div className='flex-1'>
              <Progress
                color={'destructive'}
                value={(total1Ratings / Math.max(reviews.length, 1)) * 100}
              ></Progress>
            </div>
          </li>
        </ul>
      </div>

      {state == AttendantProfileState.APPROVED && (
        <div className='w-full border-b py-4 pt-0'>
          <Button
            size={'xs'}
            onClick={() => {
              if (isAuthed) {
                setOpenReviewState(null);
              } else if (authLink) {
                router.push(authLink);
              }
            }}
            variant={'outline'}
            className='inline-flex w-full lg:w-fit'
          >
            <Star></Star>
            {reviewCreateMessage()}
          </Button>
        </div>
      )}

      <div className='flex w-full flex-col gap-4'>
        {isLoading && (
          <div className='relative flex items-center justify-center gap-2 px-2 py-2 uppercase text-muted-foreground'>
            <Loader2 className='h-8 w-8 animate-spin' />
          </div>
        )}

        <div className='flex flex-col gap-4'>
          {reviews.length <= 0 && !isLoading && (
            <div className='flex w-full items-center gap-2 text-muted-foreground lg:justify-center'>
              <FileSearch className='size-4'></FileSearch>
              <span className='text-sm font-medium'>{reviewNoRecords()}</span>
            </div>
          )}

          {reviews.map((df) => (
            <GirlReview
              onUpdate={() => setOpenReviewState(df)}
              onDelete={() => onDelete(df)}
              key={`review-${df.id}`}
              item={df}
            ></GirlReview>
          ))}
        </div>
      </div>
    </div>
  );
};
